import { SearchBar } from "@components/institution_settings/SearchBar";
import { NavigationAnchor } from "@components/navigation/NavigationLink";
import { PageContainer } from "@components/PageContainer";
import { Alert } from "@narmi/design_system";
import { useCallback, useMemo, useState } from "react";
import { $staffApi } from "../../api";
import styles from "./InstitutionSettings.module.css";

const SEARCH_PAGE_SIZE = 25;

/**
 * The API returns a fully qualified `next` URL. We only need its `offset`, which becomes
 * the page param for the following request.
 */
const nextOffsetFromUrl = (url?: string | null): number | undefined => {
  if (!url) return undefined;
  const offset = new URL(url, window.location.origin).searchParams.get("offset");
  const parsed = Number(offset);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const InstitutionSettingsBasePage = ({
  topBar,
  children,
}: {
  topBar: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState<string | undefined>(undefined);

  const [routeAlertOpen, setRouteAlertOpen] = useState(true);

  const {
    data: searchData,
    isLoading: isSearchLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = $staffApi.useInfiniteQuery(
    "get",
    "/api/institution_settings/search/",
    {
      params: {
        query: { q: searchQuery, category: searchCategory, limit: SEARCH_PAGE_SIZE },
      },
    },
    {
      pageParamName: "offset",
      initialPageParam: 0,
      // `next` being null is the server telling us this was the last page.
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.next
          ? (nextOffsetFromUrl(lastPage.next) ?? Number(lastPageParam) + SEARCH_PAGE_SIZE)
          : undefined,
    }
  );

  const searchResults = useMemo(
    () => searchData?.pages.flatMap((page) => page.results) ?? [],
    [searchData]
  );

  const institutionCategoriesWithCount = useMemo(() => {
    const resultCount = searchData?.pages[0]?.result_count ?? {};
    return Object.entries(resultCount).map(([category, count]) => ({
      name: category,
      setting_count: count,
    }));
  }, [searchData]);

  const loadMoreSearchResults = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      {topBar}
      <PageContainer>
        <div className={styles.settingsContainer}>
          {routeAlertOpen && (
            <div className="margin--bottom--xl">
              <Alert isActive onUserDismiss={() => setRouteAlertOpen(false)} kind="info">
                <span>
                  This is the new institution settings experience. To use the old one, click{" "}
                  <NavigationAnchor to="/institution_settings">here</NavigationAnchor>
                </span>
              </Alert>
            </div>
          )}
          <div className={styles.searchBarContainer}>
            <SearchBar
              onDebouncedChange={setSearchQuery}
              onSelectSearchCategory={setSearchCategory}
              searchResults={searchResults}
              totalResultCount={searchData?.pages[0]?.count}
              isSearchLoading={isSearchLoading}
              institutionCategoriesWithCount={institutionCategoriesWithCount ?? []}
              searchCategory={searchCategory}
              hasNextPage={!!hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={loadMoreSearchResults}
            />
          </div>
          {children}
        </div>
      </PageContainer>
    </>
  );
};
